"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Link from "next/link"
import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { fetchApi } from "@/lib/api/client"
import { IsolatedPreview } from "@/components/phishing/preview/isolated-preview"
import type { EntityCard } from "@/lib/ai-chat/types"

/**
 * Full-size preview of something the assistant just created.
 *
 * The first version expanded inline inside the chat. That was the wrong
 * surface: the companion is a picture-in-picture panel or a narrow docked
 * drawer, so a rendered email came out a few hundred pixels wide — which is
 * not a preview of an email. A lure has to be seen at roughly the size its
 * recipient will see it, which means taking over the screen.
 *
 * Note this component does NOT touch the modal counter in `ui-store`.
 * `FabStackHost` observes `[data-slot="dialog-content"][data-state="open"]`
 * and maintains that count itself; pushing again here would leave it at 1
 * after this closes and hide the floating buttons for the rest of the session.
 */

/** Shape of `GET /v1/phishing/scenarios/{id}/preview`. */
interface ScenarioPreview {
  subject?: string
  senderName?: string
  senderEmail?: string
  htmlContent?: string
  landingPageHtml?: string
}

export function EntityPreviewModal({
  entity,
  open,
  onOpenChange,
}: {
  entity: EntityCard
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const [preview, setPreview] = useState<ScenarioPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  // Guards against React re-invoking the effect (StrictMode double-mount, a
  // parent re-render) turning one open into several identical fetches.
  const requested = useRef(false)

  const load = useCallback(async (path: string) => {
    setLoading(true)
    setFailed(false)
    try {
      setPreview(await fetchApi<ScenarioPreview>(path))
    } catch {
      // No error detail surfaced: this is a preview, and the useful next step
      // is the same whatever went wrong — open the page itself.
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open || !entity.previewPath || requested.current) return
    requested.current = true
    void load(entity.previewPath)
  }, [open, entity.previewPath, load])

  const landing = preview?.landingPageHtml?.trim()
  const body = preview?.htmlContent?.trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[88vh] grid-rows-[auto_minmax(0,1fr)] p-0 gap-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border text-start">
          <DialogTitle className="text-base font-semibold pe-8">
            {entity.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Preview of the {entity.kind} the assistant created.
          </DialogDescription>

          {preview && !failed && (
            <dl className="mt-2 grid gap-0.5 text-xs">
              {preview.senderName && (
                <div className="flex gap-1.5 min-w-0">
                  <dt className="text-muted-foreground shrink-0">From</dt>
                  <dd className="truncate">
                    {preview.senderName}
                    {preview.senderEmail ? ` <${preview.senderEmail}>` : ""}
                  </dd>
                </div>
              )}
              {preview.subject && (
                <div className="flex gap-1.5 min-w-0">
                  <dt className="text-muted-foreground shrink-0">Subject</dt>
                  <dd className="truncate font-medium">{preview.subject}</dd>
                </div>
              )}
            </dl>
          )}

          <Link
            href={entity.href}
            className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline w-fit"
          >
            Open {entity.kind}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </Link>
        </DialogHeader>

        <div className="overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading preview…
            </div>
          )}

          {failed && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm">
              <AlertTriangle
                className="h-4 w-4 mt-0.5 shrink-0 text-destructive"
                aria-hidden="true"
              />
              <p className="text-muted-foreground">
                Couldn&apos;t load the preview. Open the {entity.kind} to view it.
              </p>
            </div>
          )}

          {preview && !failed && !body && !landing && (
            /* Nothing to render. A scenario created from chat has a name,
               subject and sender but no email template until one is authored,
               so an empty pane here reads as "the preview is broken" rather
               than "there is nothing here yet". Empty content and a failed
               fetch are different states and get different words. */
            <p className="py-6 text-center text-sm text-muted-foreground">
              This {entity.kind} has no email body yet. Open it to add one.
            </p>
          )}

          {preview && !failed && body && !landing && (
            /* Email only — a single pane needs no tab strip. */
            <PreviewPane html={body} />
          )}

          {preview && !failed && landing && (
            <Tabs defaultValue="email">
              <TabsList className="mb-3">
                <TabsTrigger value="email">Email</TabsTrigger>
                {/* Half of reviewing a scenario is "does the landing page look
                    right" — and it is just as attacker-shaped as the email. */}
                <TabsTrigger value="landing">Landing page</TabsTrigger>
              </TabsList>
              <TabsContent value="email">
                {body ? (
                  <PreviewPane html={body} />
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    This {entity.kind} has no email body yet. Open it to add one.
                  </p>
                )}
              </TabsContent>
              <TabsContent value="landing">
                <PreviewPane html={landing} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Everything rendered here is lure content authored to be convincing.
 * `IsolatedPreview` puts it in a sandboxed `<iframe srcdoc>`; it must never
 * reach the admin DOM.
 */
function PreviewPane({ html }: { html: string }) {
  return (
    <div className="rounded-md border border-border bg-background overflow-hidden">
      <IsolatedPreview html={html} minHeight={280} />
    </div>
  )
}
